import { useEffect, useRef, useState } from 'react';
import { GAME_RULES } from '@continuum/show-core';
import type { RecallTask } from '@continuum/show-core';
import type { GameProps } from './types.js';
import { mono } from '../ui/atoms.js';
import { RelicBack } from './art.js';

interface Card {
  id: number;
  glyph: string;
  faceUp: boolean;
  matched: boolean;
  burnt: boolean;
}

/**
 * ARCHIVIST — flip the record, match the pairs.
 *
 * Concentration, because archiving *is* remembering. In 2398 the archive is burning:
 * unmatched cards start going for good, so what you save is what you looked at.
 */
export function Recall({ task, era, onProgress, onWin, onMistake, accent, onFrame, paused = false, s }: GameProps<RecallTask>) {
  const { flipBackMs, matchMs } = GAME_RULES.recall;
  const [cards, setCards] = useState<Card[]>(() => {
    const glyphs = task.glyphs.slice(0, task.pairs);
    const deck = [...glyphs, ...glyphs].map((glyph, id) => ({
      id,
      glyph,
      faceUp: false,
      matched: false,
      burnt: false,
    }));
    for (let i = deck.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [deck[i], deck[j]] = [deck[j]!, deck[i]!];
    }
    return deck;
  });
  const [busy, setBusy] = useState(false);
  const won = useRef(false);
  /** Cards that have been face up at least once, so a mismatch is only a mistake when it could have been avoided. */
  const seen = useRef(new Set<number>());
  const cbs = useRef({ onProgress, onWin, onFrame, onMistake });
  cbs.current = { onProgress, onWin, onFrame, onMistake };
  const hold = useRef(paused);
  hold.current = paused;

  // The fire: one unmatched card at a time, and never the last pair that can still be saved, so
  // the board always ends on a match and never burns out by itself.
  useEffect(() => {
    if (task.burnAfterMs === null) return;
    const id = setInterval(() => {
      // Nothing burns while something covers the board.
      if (hold.current) return;
      setCards((prev) => {
        const alive = prev.filter((c) => !c.matched && !c.burnt);
        const savable = new Set(alive.filter((c) => alive.some((d) => d.id !== c.id && d.glyph === c.glyph)).map((c) => c.glyph));
        if (savable.size <= 1) return prev;
        const victim = alive[Math.floor(Math.random() * alive.length)]!;
        // Only burn a card whose twin is still there: its twin is lost with it, and is no longer needed.
        const twinAlive = alive.some((c) => c.id !== victim.id && c.glyph === victim.glyph);
        if (!twinAlive) return prev;
        return prev.map((c) => (c.id === victim.id ? { ...c, burnt: true } : c));
      });
    }, task.burnAfterMs);
    return () => clearInterval(id);
  }, [task.burnAfterMs]);

  const flip = (card: Card) => {
    if (hold.current || busy || card.faceUp || card.matched || card.burnt) return;
    const faceUp = cards.filter((c) => c.faceUp && !c.matched);
    const next = cards.map((c) => (c.id === card.id ? { ...c, faceUp: true } : c));
    setCards(next);
    // Seen before this turn, for judging a mismatch below.
    const known = seen.current.has(card.id);
    seen.current.add(card.id);
    if (faceUp.length === 0) return;

    const first = faceUp[0]!;
    setBusy(true);
    if (first.glyph === card.glyph) {
      setTimeout(() => {
        setCards((prev) => {
          const done = prev.map((c) =>
            c.glyph === card.glyph ? { ...c, matched: true, faceUp: true } : c,
          );
          const pairs = new Set(done.filter((c) => c.matched).map((c) => c.glyph)).size;
          // In a burning archive a pair can be lost to the fire. Once every pair is filed or
          // lost, there is nothing left to save and the station is done.
          const settled = new Set(done.filter((c) => c.matched || c.burnt).map((c) => c.glyph)).size;
          cbs.current.onProgress(Math.round((pairs / task.pairs) * 100));
          if (settled >= task.pairs && !won.current) {
            won.current = true;
            // Not inside the state update: WELL DONE is another component's state.
            queueMicrotask(() => cbs.current.onWin());
          }
          return done;
        });
        setBusy(false);
      }, matchMs);
      return;
    }
    // Two strangers turned over is how the game is played, not a mistake. It is one when the guest
    // could have known: they had already seen the first card's twin, or this very card.
    const twinSeen = cards.some((c) => c.id !== first.id && c.glyph === first.glyph && !c.burnt && seen.current.has(c.id));
    if (twinSeen || known) cbs.current.onMistake?.();
    setTimeout(() => {
      setCards((prev) => prev.map((c) => (c.matched ? c : { ...c, faceUp: false })));
      setBusy(false);
    }, flipBackMs);
  };

  // Which cards are face up, for anyone watching over a shoulder.
  useEffect(() => {
    cbs.current.onFrame(
      cards
        .filter((c) => c.faceUp)
        .map((c) => `u${c.id}`)
        .join(','),
    );
  }, [cards]);

  const cols = task.pairs <= 3 ? 3 : 4;
  const matched = new Set(cards.filter((c) => c.matched).map((c) => c.glyph)).size;

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols},1fr)`, gap: 8 }}>
        {cards.map((card) => (
          <button
            key={card.id}
            onPointerDown={() => flip(card)}
            disabled={card.burnt}
            style={{
              aspectRatio: '3 / 4',
              borderRadius: 10,
              border: `1.5px solid ${card.burnt ? 'rgba(255,46,63,.3)' : card.matched ? accent : `${accent}44`}`,
              background: card.burnt
                ? 'repeating-linear-gradient(45deg,rgba(255,46,63,.06) 0 4px,transparent 4px 8px)'
                : card.faceUp
                  ? `${accent}1c`
                  : 'rgba(255,255,255,.04)',
              color: card.burnt ? 'rgba(255,46,63,.45)' : accent,
              fontSize: 26,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: card.matched ? 0.55 : 1,
              transition: 'background .18s, opacity .3s',
              touchAction: 'none',
            }}
          >
            {card.burnt ? (
              '✕'
            ) : card.faceUp ? (
              <span style={{ animation: 'ccPop .22s ease-out' }}>{card.glyph}</span>
            ) : (
              // Face down, the card shows an archive sleeve rather than nothing at all.
              <span style={{ width: '78%', height: '78%', display: 'block' }}>
                <RelicBack era={era} />
              </span>
            )}
          </button>
        ))}
      </div>

      <div style={{ marginTop: 10, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '4px 10px' }}>
        <span style={{ fontFamily: mono, fontSize: 13, letterSpacing: 1.5, color: accent }}>
          {s.recallPairs(matched, task.pairs)}
        </span>
        <div style={{ flex: 1 }} />
        {task.burnAfterMs !== null && (
          <span
            style={{
              fontFamily: mono,
              fontSize: 12,
              letterSpacing: 1,
              color: '#ff9a4a',
            }}
          >
            {s.recallBurning}
          </span>
        )}
      </div>
    </div>
  );
}
